import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PageProps } from "../types/auth";

import styles from './styles/GroupAttendancePage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Member {
  user_id: number;
  username: string;
  display_name: string;
  last_attendance_date?: string;
}

interface Group {
  name: string;
  curriculum_id?: number;
  members?: Member[];
}

interface Curriculum {
  curriculum_id: number;
  name: string;
}

interface RouteParams {
  [key: string]: string | undefined;
  group_id?: string;
}

interface AttendanceRecord {
  [key: number]: boolean;
}

const GroupAttendancePage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const { group_id } = useParams<RouteParams>();
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState<string>("");
  const [curriculums, setCurriculums] = useState<Curriculum[]>([]);
  const [selectedCurriculum, setSelectedCurriculum] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [groupMembers, setGroupMembers] = useState<Member[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord>({});
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const fetchGroupDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/groups/${group_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch group details.");
          return;
        }

        const data = await response.json();
        setGroupName(data.name || "");
        if (data.curriculum_id) {
          setSelectedCurriculum(data.curriculum_id.toString());
        }
        
        // Use the members data from the group details response
        if (data.members && Array.isArray(data.members)) {
          const members = data.members || [];
          setGroupMembers(members);
          setAttendance(
            members.reduce((acc: AttendanceRecord, member: Member) => {
              acc[member.user_id] = false;
              return acc;
            }, {} as AttendanceRecord)
          );
        }
      } catch (err) {
        setError("An unexpected error occurred while fetching group details.");
      }
    };

    const fetchCurriculums = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/curriculums`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch curriculums.");
          return;
        }

        const data: Curriculum[] = await response.json();
        setCurriculums(data || []);
      } catch (err) {
        setError("An unexpected error occurred while fetching curriculums.");
      }
    };

    if (group_id) {
      fetchGroupDetails();
      fetchCurriculums();
    }
  }, [group_id, token, selectedOrgID]);

  const handleAttendanceChange = (userId: number) => {
    setAttendance((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const markAttendance = async () => {
    if (!selectedCurriculum) {
      setError("Please select a curriculum.");
      return;
    }

    // Get timezone offset in hours:minutes format
    const date = new Date()
    const offsetMinutes = date.getTimezoneOffset()
    const offsetHours = Math.abs(Math.floor(offsetMinutes / 60)).toString().padStart(2, "0")
    const offsetMins = Math.abs(offsetMinutes % 60).toString().padStart(2, "0")
    const offsetSign = offsetMinutes <= 0 ? "+" : "-"
    
    // Combine selected date with midnight time and timezone
    const localISOString = `${selectedDate}T00:00:00${offsetSign}${offsetHours}:${offsetMins}`

    const curriculumId = parseInt(selectedCurriculum, 10);
    const requests = Object.entries(attendance)
      .filter(([, attended]) => attended)
      .map(([userId]) => {
        const userIdInt = parseInt(userId, 10);
        return fetch(`${API_BASE_URL}/api/attendances`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
          body: JSON.stringify({
            user_id: userIdInt,
            curriculum_id: curriculumId,
            attendance_date: localISOString,
          }),
        });
      });

    try {
      await Promise.all(requests);
      navigate(`/groups/${group_id}`);
    } catch (err) {
      setError("An error occurred while marking attendance.");
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Mark Attendance</h1>
      {error && <p className={styles.errorMessage}>{error}</p>}

      <div className={styles.groupDetails}>
        <strong>Group:</strong>{" "}
        <Link to={`/groups/${group_id}`} className={styles.groupLink}>
          {groupName}
        </Link>
      </div>
      <div className={styles.curriculumSelection}>
        <strong>Curriculum:</strong>
        <select
          value={selectedCurriculum}
          onChange={(e) => setSelectedCurriculum(e.target.value)}
          disabled={curriculums.length === 0}
          className={styles.select}
        >
          <option value="" disabled>
            {curriculums.length === 0 ? "No curriculums available" : "Select a curriculum"}
          </option>
          {curriculums.map((curriculum) => (
            <option key={curriculum.curriculum_id} value={curriculum.curriculum_id}>
              {curriculum.name}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.datePicker}>
        <strong>Date:</strong>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className={styles.input}
        />
      </div>
      <div className={styles.membersContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.tableHeader}>Member</th>
              <th className={styles.tableHeader}>Last Attendance</th>
              <th className={styles.tableHeader}>Attended</th>
            </tr>
          </thead>
          <tbody>
            {groupMembers.length > 0 ? (
              groupMembers.map((member) => (
                <tr key={member.user_id} className={styles.tableRow}>
                  <td className={styles.tableCell}>
                    <Link to={`/members/${member.user_id}`} className={styles.memberLink}>
                      {member.display_name} ({member.username})
                    </Link>
                  </td>
                  <td className={styles.tableCell}>
                    {member.last_attendance_date 
                      ? new Date(member.last_attendance_date).toLocaleDateString()
                      : "-"}
                  </td>
                  <td className={styles.tableCell}>
                    <input
                      type="checkbox"
                      checked={attendance[member.user_id] || false}
                      onChange={() => handleAttendanceChange(member.user_id)}
                      className={styles.checkbox}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className={styles.tableCell}>No members found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <button
        className={styles.markAttendanceButton}
        onClick={markAttendance}
        disabled={!selectedCurriculum}
      >
        Mark Attendance
      </button>
    </div>
  );
};

export default GroupAttendancePage;
